// apps/web/src/lib/kanban/dispositivo.ts — Fase 11, Plano 15 (KAN-06, D-21, ADR-003 §13.4).
//
// Credencial do dispositivo (tablet do chão de fábrica) guardada em IndexedDB
// via Dexie. Nunca em armazenamento chave/valor do navegador: a credencial só
// trafega em cabeçalhos para a própria origem (T-11-55).
import Dexie, { type Table } from 'dexie';
import type { KanbanTabletQuadro } from '@pieltec/contracts';

export interface CredencialDispositivo {
  deviceId: string;
  credential: string;
  label: string;
  criadoEm: number;
}

interface RegistroCredencial extends CredencialDispositivo {
  chave: 'atual';
}

interface RegistroQuadroCache {
  chave: 'atual';
  quadro: KanbanTabletQuadro;
  salvoEm: number;
}

class PieltecKanbanDispositivoDb extends Dexie {
  credencial!: Table<RegistroCredencial, string>;
  quadroCache!: Table<RegistroQuadroCache, string>;

  constructor() {
    super('PieltecKanbanDispositivoDb');
    this.version(1).stores({
      credencial: 'chave',
      quadroCache: 'chave',
    });
  }
}

let banco: PieltecKanbanDispositivoDb | null = null;

function db(): PieltecKanbanDispositivoDb {
  if (!banco) banco = new PieltecKanbanDispositivoDb();
  return banco;
}

export async function obterCredencial(): Promise<CredencialDispositivo | null> {
  const registro = await db().credencial.get('atual');
  if (!registro) return null;
  const { deviceId, credential, label, criadoEm } = registro;
  return { deviceId, credential, label, criadoEm };
}

export async function salvarCredencial(dados: Omit<CredencialDispositivo, 'criadoEm'>): Promise<void> {
  await db().credencial.put({ ...dados, chave: 'atual', criadoEm: Date.now() });
}

export async function limparCredencial(): Promise<void> {
  await db().credencial.delete('atual');
  await db().quadroCache.delete('atual');
}

/** Último quadro recebido: o tablet offline mostra isto em vez de skeleton (KAN-08). */
export async function salvarQuadroCache(quadro: KanbanTabletQuadro): Promise<void> {
  await db().quadroCache.put({ chave: 'atual', quadro, salvoEm: Date.now() });
}

export async function obterQuadroCache(): Promise<KanbanTabletQuadro | null> {
  const registro = await db().quadroCache.get('atual');
  return registro ? registro.quadro : null;
}

/** Cabeçalhos exigidos pelo KanbanDispositivoGuard e pelo DeviceSyncGuard. */
export function cabecalhosDispositivo(
  credencial: Pick<CredencialDispositivo, 'deviceId' | 'credential'> | null,
): Record<string, string> {
  if (!credencial) return {};
  return {
    'x-device-id': credencial.deviceId,
    'x-device-credential': credencial.credential,
  };
}

/** 401 revogada/inválida (ou expirada) exige purgar a credencial local (RF-92). */
export function devePurgarCredencial(resposta: { status: number; reason?: string }): boolean {
  if (resposta.status !== 401) return false;
  return resposta.reason === 'revoked' || resposta.reason === 'invalid' || resposta.reason === 'expired';
}

/** Validade padrão da credencial do tablet: 1 ano a partir de agora. */
export function expiracaoPadrao(agora: Date): string {
  const d = new Date(agora.getTime());
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  return d.toISOString();
}
